import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { debounce, onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * GW (visual-overdrive P2 r4b): background regions the open drawer OCCLUDES.
 *
 * The drawer is a `<details>` panel, not a modal `<dialog>` — so unlike the cart
 * drawer and the search modal (both `showModal()`, which the browser backs with
 * the "blocked by a modal dialog" mechanism and therefore get inertness for
 * free), nothing takes the rest of the page out of the accessibility tree when
 * it opens. At 360 the panel covers x 0-342 of a 360 viewport at full height,
 * so the whole page is behind it, still tabbable and still scannable: P2 r4's
 * drawer-open axe run reported 2 serious `color-contrast` nodes on hero text
 * that no sighted user can see, composited against the DRAWER's own Prairie
 * Cream panel and Copper Glory button. A 4-arm A/B (evidence/p2-r4/
 * axe-drawer-ab.json) proved the hero was not the cause; the missing `inert`
 * was.
 *
 * Deliberately NOT listed: `#header-group` (contains the drawer itself), the
 * skip-to-content link (an escape hatch, not occluded page content), and the
 * `<dialog>` wrappers, which render nothing while closed and must stay
 * un-inerted so a modal can still be opened.
 *
 * @type {string}
 */
const OCCLUDED_BACKGROUND_SELECTOR = '#MainContent, body > footer';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /**
   * Watches the drawer's own open state so background inertness is derived from
   * the DOM rather than enumerated per close path.
   *
   * `close()` is reached from the summary, the close button, the backdrop, the
   * submenu back/close buttons and Escape — and every one of them ends in
   * `reset()` stripping the `open` attribute. Observing the attribute instead of
   * hooking six call sites means a close path that is added later cannot forget
   * to clear `inert` and leave the page dead.
   *
   * @type {MutationObserver}
   */
  #openStateObserver = new MutationObserver(() => this.#syncBackgroundInert());

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    this.#openStateObserver.observe(this.refs.details, {
      attributes: true,
      attributeFilter: ['open', 'class'],
    });
    window.addEventListener('resize', this.#onViewportResize);
    this.#syncBackgroundInert();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);

    this.#openStateObserver.disconnect();
    window.removeEventListener('resize', this.#onViewportResize);
    // A section re-render must never strand `inert` on a page with no drawer
    // left to remove it.
    this.#setBackgroundInert(false);
  }

  /**
   * Whether the drawer is actually rendered. `.header__drawer` is
   * `display: none` at >=750px when the header resolves to the mega-menu
   * (blocks/_header-menu.liquid), so an open drawer can be hidden out from
   * under itself by a resize.
   *
   * @returns {boolean}
   */
  get #isRendered() {
    const details = /** @type {HTMLElement} */ (this.refs.details);

    return typeof details.checkVisibility === 'function'
      ? details.checkVisibility()
      : details.getClientRects().length > 0;
  }

  /**
   * Apply or clear `inert` on the regions the open drawer covers.
   *
   * @param {boolean} shouldBeInert
   */
  #setBackgroundInert(shouldBeInert) {
    for (const region of document.querySelectorAll(OCCLUDED_BACKGROUND_SELECTOR)) {
      region.toggleAttribute('inert', shouldBeInert);
    }
  }

  /**
   * Derive background inertness from the drawer's state.
   *
   * The condition is `open` AND `menu-open` because that is EXACTLY the
   * condition under which the panel is on screen: `open` is what makes the
   * details content render at all, and `.menu-open > .menu-drawer` (the
   * stylesheet in snippets/header-drawer.liquid) is what moves it in from
   * `translateX(-100%) / visibility: hidden`. Anything looser marks a page
   * inert that nothing is covering.
   *
   * That is not theoretical. `||` was tried first, on the reasoning that the
   * two signals fall away at different moments, and the r4b harness caught it
   * reaching a state of `open: false, menuOpen: true` — panel hidden at
   * x: -160, background inert, page dead. Whatever produces that state (it
   * survives from stock Horizon: `open()` schedules the class in a
   * requestAnimationFrame, so a `close()` landing between the click and the
   * frame leaves the class to be re-added after the reset), `&&` is inert-safe
   * through it.
   *
   * The cost is that closing from the backdrop, the close button or Escape
   * clears `inert` when the class drops — at the START of the ~300ms slide-out
   * rather than its end. The background is briefly interactive under a panel
   * that is still leaving. That is the right side to err on: a page that
   * un-inerts a moment early is usable, one that un-inerts a moment late is
   * not.
   */
  #syncBackgroundInert() {
    const { details } = this.refs;

    if (!details) return;

    const isShowing = details.hasAttribute('open') && details.classList.contains('menu-open');

    this.#setBackgroundInert(isShowing && this.#isRendered);
  }

  /**
   * Close a drawer that a viewport change has hidden.
   *
   * Debounced past `setHeaderMenuStyle()`'s own 100ms debounce and its
   * requestAnimationFrame, so `data-menu-style` has already flipped by the time
   * visibility is measured. Mirrors `DialogComponent`'s `#handleResize`, which
   * closes the cart drawer when it leaves its width range.
   */
  #onViewportResize = debounce(() => {
    if (!this.refs.details || !this.isOpen || this.#isRendered) return;

    this.close();
    this.#syncBackgroundInert();
  }, 150);

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
